import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Services} from '@src/app/core/enums/services.enum';
import {SiteFormSummary} from '@src/app/admin/sites/models/site.model';
import {SiteFormsService} from '@src/app/admin/sites/services/site-forms.service';
import {ApiService} from '@src/app/shared/services/api.service';
import {forkJoin, Subscription} from 'rxjs';

interface PreWorkSettingsForm
{
    vehicle_check_enabled: FormControl<boolean>;
    vehicle_check_form_id: FormControl<string | null>;
    enforcement: FormControl<'block' | 'warn'>;
}

@Component({
    standalone: false,
    selector: 'app-pre-work-settings',
    templateUrl: './pre-work-settings.component.html',
})
export class PreWorkSettingsComponent implements OnInit, OnDestroy
{
    public readonly form = new FormGroup<PreWorkSettingsForm>({
        vehicle_check_enabled: new FormControl(false, {nonNullable: true}),
        vehicle_check_form_id: new FormControl<string | null>(null),
        enforcement: new FormControl<'block' | 'warn'>('block', {nonNullable: true}),
    });
    public vehicleCheckForms: SiteFormSummary[] = [];
    public loading = true;
    public saving = false;
    public loadFailed = false;
    private readonly subscriptions = new Subscription();

    public constructor(
        private readonly apiService: ApiService,
        private readonly siteFormsService: SiteFormsService,
        private readonly snackBar: MatSnackBar,
    ) {}

    public ngOnInit(): void
    {
        this.subscriptions.add(this.form.controls.vehicle_check_enabled.valueChanges.subscribe(() => {
            this.configureFormValidator();
        }));
        this.configureFormValidator();
        this.load();
    }

    public ngOnDestroy(): void
    {
        this.subscriptions.unsubscribe();
    }

    public save(): void
    {
        this.form.markAllAsTouched();
        if (this.form.invalid || this.saving) return;

        this.saving = true;
        this.apiService.updateSettings('settings/pre_work', this.form.getRawValue()).subscribe({
            next: response => {
                this.applySettings(response?.data ?? []);
                this.saving = false;
                this.snackBar.open('Pre-work check settings saved.', 'Ok', {duration: 2500});
            },
            error: error => {
                this.saving = false;
                this.apiService.handleError(error);
            },
        });
    }

    private load(): void
    {
        this.loading = true;
        this.loadFailed = false;
        forkJoin({
            settings: this.apiService.list('settings'),
            formGroups: this.siteFormsService.loadFormGroups(),
        }).subscribe({
            next: ({settings, formGroups}) => {
                this.vehicleCheckForms = formGroups
                    .flatMap(group => group.forms)
                    .filter(form => String(form.service?.id) === Services.VehicleCheck)
                    .sort((left, right) => left.title.localeCompare(right.title));
                this.applySettings(settings?.data ?? []);
                this.loading = false;
            },
            error: error => {
                this.loading = false;
                this.loadFailed = true;
                this.apiService.handleError(error);
            },
        });
    }

    private applySettings(settings: any[]): void
    {
        const preWork = settings.filter(setting => setting.feature === 'pre_work');
        const value = (name: string, fallback: any): any => preWork.find(setting => setting.name === name)?.value ?? fallback;
        this.form.patchValue({
            vehicle_check_enabled: this.asBoolean(value('vehicle_check_enabled', false)),
            vehicle_check_form_id: value('vehicle_check_form_id', null),
            enforcement: value('enforcement', 'block') === 'warn' ? 'warn' : 'block',
        }, {emitEvent: false});
        this.configureFormValidator();
    }

    private configureFormValidator(): void
    {
        const control = this.form.controls.vehicle_check_form_id;
        control.setValidators(this.form.controls.vehicle_check_enabled.value ? [Validators.required] : []);
        control.updateValueAndValidity({emitEvent: false});
    }

    private asBoolean(value: unknown): boolean
    {
        return value === true || value === 1 || value === '1' || value === 'true';
    }
}
